import { useCallback, useEffect, useRef } from "react";
import { Platform } from "react-native";

type ScrollableListRef = {
  scrollToIndex?: (options: {
    animated?: boolean;
    index: number;
    viewPosition?: number;
    viewOffset?: number;
  }) => void;
  scrollToOffset?: (options: { animated?: boolean; offset: number }) => void;
};

type TeeSheetComponent = {
  startAt?: string | null;
};

type ScrollToTimeSlotHourArgs = {
  startHourParam?: number | string;
  teeSheetComponents: TeeSheetComponent[];
  flatListRef: React.RefObject<ScrollableListRef | null>;
  flashListRef: React.RefObject<ScrollableListRef | null>;
  viewOffset?: number;
  retryDelayMs?: number;
  averageItemLength?: number;
};

export const useScrollToTimeSlotHour = ({
  startHourParam,
  teeSheetComponents,
  flatListRef,
  flashListRef,
  viewOffset = 24,
  retryDelayMs = 100,
}: ScrollToTimeSlotHourArgs) => {
  const pendingScrollIndexRef = useRef<number | null>(null);

  const scrollToIndex = useCallback(
    (index: number) => {
      const listRef =
        Platform.OS === "web" ? flatListRef.current : flashListRef.current;
      pendingScrollIndexRef.current = index;
      setTimeout(() => {
        listRef?.scrollToIndex?.({
          animated: true,
          index,
          viewPosition: 0,
          viewOffset,
        });
      }, retryDelayMs);
    },
    [flashListRef, flatListRef, retryDelayMs, viewOffset]
  );

  useEffect(() => {
    const startHour =
      typeof startHourParam === "string"
        ? Number.parseInt(startHourParam, 10)
        : startHourParam;
    if (!Number.isFinite(startHour)) {
      return;
    }
    const itemIndex = teeSheetComponents.findIndex((ts) => {
      const hourString = ts.startAt?.slice(0, 2);
      const hour = hourString ? Number.parseInt(hourString, 10) : NaN;
      return Number.isFinite(hour) && hour === startHour;
    });
    if (itemIndex !== -1) {
      setTimeout(() => {
        scrollToIndex(itemIndex);
      }, retryDelayMs);
    }
  }, [retryDelayMs, scrollToIndex, startHourParam, teeSheetComponents]);
};
